import { forwardRef, useEffect, useMemo, useRef, useState, useCallback, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { ElementPalette } from './ElementPalette';
import { BlockBuilderDialog } from './BlockBuilderDialog';
import { parseBlockPatchFromHash } from './aiBlockPatch';
import { mechanismToPlacedType } from './blockBuilder';
import { DoubleSwitchDialog } from './DoubleSwitchDialog';
import { MasterSwitchDialog } from './MasterSwitchDialog';
import { FloorPlanCanvas, type FloorPlanCanvasHandle } from './FloorPlanCanvas';
import { ImageCleanupDialog } from './ImageCleanupDialog';
import { LinesPanel } from './LinesPanel';
import { SpecificationDialog } from './SpecificationDialog';
import { createDimensionStore } from './dimensions';
import { createDimensionTool, type DimensionPlacementMode, type DimensionPreview, type ToolMode } from './dimension-tool';
import { ELEMENT_CATALOG } from './icons';
import { validateImageFile } from './imageValidation';
import type { FloorPlanLayer } from './layers';
import { computeBackgroundHash, createMetrics, makeUserCalibration, type Pt } from './metrics';
import type { PlanProject } from './projectFile';
import { buildElectricalSpecification } from './specification';
import { KW_MAP, GROUP_MAP } from './symbols';
import { formatLengthMm, formatScaleMmPerPx, normalizeUnitLocale, parseLengthMm } from './units';
import {
  LINE_COLORS,
  type BgTransform,
  type CustomBlockPayload,
  type Dimension,
  type Line,
  type MasterSwitchMode,
  type PlacedItem,
  type PlanCalibration,
  type Transform,
} from './types';

interface FloorPlanEditorProps {
  items: PlacedItem[];
  onItemsChange: (items: PlacedItem[]) => void;
  onProjectStateChange?: () => void;
  onClearProject?: () => void;
  onBackgroundCleared?: () => void;
}

interface PendingAiBlockPatch {
  item: PlacedItem;
  payload: CustomBlockPayload;
}

interface EditorHistoryState {
  items: PlacedItem[];
  dimensions: Dimension[];
  calibration: PlanCalibration | null;
}

interface DimensionContextMenuState {
  id: string;
  x: number;
  y: number;
  labelInput: string;
}

export type PlanProjectState = PlanProject;

export interface FloorPlanEditorHandle {
  getState: () => Omit<PlanProjectState, 'items'>;
  loadState: (plan: PlanProjectState) => void;
  exportBackgroundDataUrl: () => Promise<string | null>;
  openSpecification: () => void;
}

function isLineControlledItemType(type: string): boolean {
  const group = ELEMENT_CATALOG[type]?.group;
  return group === 'light' || type === 'switch';
}

function computeStats(items: PlacedItem[]) {
  let light = 0, socket = 0, sw = 0, appliance = 0, kw = 0;
  for (const it of items) {
    const g = GROUP_MAP[it.type];
    if (g === 'light')     light++;
    else if (g === 'socket')    socket++;
    else if (g === 'switch')    sw++;
    else if (g === 'appliance') appliance++;
    kw += KW_MAP[it.type] ?? 0;
  }
  return { light, socket, sw, appliance, kw };
}

const CLEANUP_AUTO_OPEN_SIZE = 200 * 1024;
const DEFAULT_LINE_ID = 'L1';
const DEFAULT_BG_TRANSFORM: BgTransform = { x: 0, y: 0, scale: 1 };
const FALLBACK_BLOCK_ITEM_SPACING = 0.025;
const BLOCK_SLOT_SPACING_FACTOR = 0.50;

async function createObjectUrlFromDataUrl(dataUrl: string): Promise<string> {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

function imageToPngDataUrl(image: HTMLImageElement): string {
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context unavailable');
  ctx.drawImage(image, 0, 0, width, height);
  return canvas.toDataURL('image/png');
}

function isTextEditingTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.tagName === 'BUTTON' ||
    target.isContentEditable
  );
}

function clampPlanPosition(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function blockAxisPositions(anchor: number, count: number, spacing: number): number[] {
  if (count <= 1) return [clampPlanPosition(anchor)];

  const total = (count - 1) * spacing;
  const start = Math.min(1 - total, Math.max(0, anchor - total / 2));
  return Array.from({ length: count }, (_, index) => clampPlanPosition(start + index * spacing));
}

function getBlockGroupId(item: PlacedItem): string | null {
  return item.blockId?.trim() || null;
}

function getBlockItemLineId(type: string, mechanismLineId?: string | null, activeLineId?: string | null): string | null {
  if (type === 'switch_double') return null;
  if (type === 'switch') return mechanismLineId ?? activeLineId ?? null;
  return isLineControlledItemType(type) ? activeLineId ?? null : null;
}

function reflowBlockGroups(
  items: PlacedItem[],
  nextIconSize: number,
  metrics: { width: number; height: number } | null | undefined,
): PlacedItem[] {
  const grouped = new Map<string, PlacedItem[]>();
  for (const item of items) {
    const blockId = getBlockGroupId(item);
    if (!blockId) continue;
    grouped.set(blockId, [...(grouped.get(blockId) ?? []), item]);
  }

  if (grouped.size === 0) return items;

  const uSpacing = metrics?.width
    ? (nextIconSize * BLOCK_SLOT_SPACING_FACTOR) / metrics.width
    : FALLBACK_BLOCK_ITEM_SPACING;
  const vSpacing = metrics?.height
    ? (nextIconSize * BLOCK_SLOT_SPACING_FACTOR) / metrics.height
    : FALLBACK_BLOCK_ITEM_SPACING;
  const nextById = new Map<string, Pick<PlacedItem, 'u' | 'v'>>();

  for (const groupItems of grouped.values()) {
    if (groupItems.length <= 1) continue;

    const minU = Math.min(...groupItems.map(item => item.u));
    const maxU = Math.max(...groupItems.map(item => item.u));
    const minV = Math.min(...groupItems.map(item => item.v));
    const maxV = Math.max(...groupItems.map(item => item.v));
    const isHorizontal = (maxU - minU) >= (maxV - minV);
    const centerU = groupItems.reduce((sum, item) => sum + item.u, 0) / groupItems.length;
    const centerV = groupItems.reduce((sum, item) => sum + item.v, 0) / groupItems.length;
    const sorted = [...groupItems].sort((a, b) => isHorizontal ? a.u - b.u : a.v - b.v);
    const uPositions = isHorizontal
      ? blockAxisPositions(centerU, sorted.length, uSpacing)
      : sorted.map(() => clampPlanPosition(centerU));
    const vPositions = isHorizontal
      ? sorted.map(() => clampPlanPosition(centerV))
      : blockAxisPositions(centerV, sorted.length, vSpacing);

    sorted.forEach((item, index) => {
      nextById.set(item.id, {
        u: uPositions[index],
        v: vPositions[index],
      });
    });
  }

  if (nextById.size === 0) return items;
  return items.map(item => {
    const nextPosition = nextById.get(item.id);
    return nextPosition ? { ...item, ...nextPosition } : item;
  });
}

function nextRotation(rotation: PlacedItem['rotation']): NonNullable<PlacedItem['rotation']> {
  if (rotation === 0) return 90;
  if (rotation === 90) return 180;
  if (rotation === 180) return 270;
  return 0;
}

export const FloorPlanEditor = forwardRef<FloorPlanEditorHandle, FloorPlanEditorProps>(function FloorPlanEditor(
  { items, onItemsChange, onProjectStateChange, onClearProject, onBackgroundCleared },
  ref,
) {
  const { t, i18n } = useTranslation();
  const unitLocale = normalizeUnitLocale(i18n.resolvedLanguage ?? i18n.language);
  const [selected, setSelected] = useState<string | null>(null);
  const [toolMode, setToolMode] = useState<ToolMode>('select');
  const [dimensionPlacementMode, setDimensionPlacementMode] = useState<DimensionPlacementMode>('free');
  const [dimensionPreview, setDimensionPreview] = useState<DimensionPreview | null>(null);
  const [pendingCalibrationSegment, setPendingCalibrationSegment] = useState<{ a: Pt; b: Pt } | null>(null);
  const [calibrationInput, setCalibrationInput] = useState('');
  const [calibrationError, setCalibrationError] = useState<string | null>(null);
  const [iconSize, setIconSize] = useState(36);
  const [gridEnabled, setGridEnabled] = useState(false);
  const [gridSize, setGridSize] = useState(40);
  const [bgImage, setBgImage] = useState<HTMLImageElement | null>(null);
  const [bgImageUrl, setBgImageUrl] = useState<string | null>(null);
  const [bgImageRaw, setBgImageRaw] = useState<string | null>(null);
  const [backgroundHash, setBackgroundHash] = useState<string | null>(null);
  const [calibration, setCalibration] = useState<PlanCalibration | null>(null);
  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [bgTransform, setBgTransform] = useState<BgTransform>(DEFAULT_BG_TRANSFORM);
  const [positioningPlan, setPositioningPlan] = useState(false);
  const [workspaceLocked, setWorkspaceLocked] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [specificationOpen, setSpecificationOpen] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [pendingRawUrl, setPendingRawUrl] = useState<string | null>(null);
  const [transform, setTransform] = useState<Transform>({ scale: 1, offsetX: 0, offsetY: 0 });
  const [history, setHistory] = useState<EditorHistoryState[]>([]);
  const [redoHistory, setRedoHistory] = useState<EditorHistoryState[]>([]);
  const [lines, setLines] = useState<Line[]>([{ id: 'L1', name: 'L1', color: LINE_COLORS[0] }]);
  const [activeLineId, setActiveLineId] = useState('L1');
  const [highlightedLines, setHighlightedLines] = useState<Set<string>>(() => new Set());
  const [hiddenLayers, setHiddenLayers] = useState<Set<FloorPlanLayer>>(() => new Set());
  const [editingDoubleSwitchItemId, setEditingDoubleSwitchItemId] = useState<string | null>(null);
  const [editingMasterSwitchItemId, setEditingMasterSwitchItemId] = useState<string | null>(null);
  const [masterOff, setMasterOff] = useState(false);
  const [savedHighlightedLines, setSavedHighlightedLines] = useState<Set<string>>(() => new Set());
  const [editingCustomBlockItemId, setEditingCustomBlockItemId] = useState<string | null>(null);
  const [creatingCustomBlockItemId, setCreatingCustomBlockItemId] = useState<string | null>(null);
  const [pendingAiBlockPatch, setPendingAiBlockPatch] = useState<PendingAiBlockPatch | null>(null);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [dimensionContextMenu, setDimensionContextMenu] = useState<DimensionContextMenuState | null>(null);
  const [dimensionHelpOpen, setDimensionHelpOpen] = useState(false);
  const [dimensionsHidden, setDimensionsHidden] = useState(false);

  const canvasRef = useRef<FloorPlanCanvasHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const rawUrlRef = useRef<string | null>(null);
  const cleanedUrlRef = useRef<string | null>(null);
  const pendingRawUrlRef = useRef<string | null>(null);
  const backgroundLoadSeqRef = useRef(0);
  const customBlockCreationBaseItemsRef = useRef<PlacedItem[] | null>(null);
  const dimensionOffsetHistoryRef = useRef<string | null>(null);

  const activeMetrics = useMemo(
    () => {
      const metrics = createMetrics({
        naturalWidth: bgImage?.naturalWidth || 1,
        naturalHeight: bgImage?.naturalHeight || 1,
      });
      if (calibration) metrics.setCalibration(calibration);
      return metrics;
    },
    [bgImage?.naturalHeight, bgImage?.naturalWidth, calibration],
  );

  const dimensionStore = useMemo(() => {
    const store = createDimensionStore(activeMetrics, {
      resolveItemAnchor: itemId => {
        const item = items.find(candidate => candidate.id === itemId);
        return item ? activeMetrics.uvToSource(item.u, item.v) : null;
      },
      formatLength: valueMm => formatLengthMm(valueMm, { locale: unitLocale }),
    });
    store.load(dimensions);
    return store;
  }, [activeMetrics, dimensions, items, unitLocale]);

  const dimensionViews = useMemo(() => dimensionStore.list(), [dimensionStore]);

  const makeHistoryState = useCallback((): EditorHistoryState => ({
    items,
    dimensions,
    calibration,
  }), [calibration, dimensions, items]);

  const pushHistory = useCallback((state?: EditorHistoryState) => {
    setHistory(h => [...h.slice(-30), state ?? makeHistoryState()]);
    setRedoHistory([]);
  }, [makeHistoryState]);

  const restoreHistoryState = useCallback((state: EditorHistoryState) => {
    onItemsChange(state.items);
    setDimensions(state.dimensions);
    setCalibration(state.calibration);
  }, [onItemsChange]);

  const dimensionTool = useMemo(() => createDimensionTool({
    metrics: activeMetrics,
    store: dimensionStore,
    getSnapTargets: () => items.map(item => ({
      id: item.id,
      point: activeMetrics.uvToSource(item.u, item.v),
    })),
    getPlacementMode: () => dimensionPlacementMode,
    getBackgroundHash: () => backgroundHash,
    onCalibrationSegment: (a, b) => {
      setPendingCalibrationSegment({ a, b });
      setCalibrationInput('');
      setCalibrationError(null);
    },
    onDimensionsChange: () => {
      pushHistory();
      setDimensions(dimensionStore.serialize());
      setDimensionsHidden(false);
      setToolMode('select');
      setDimensionPreview(null);
    },
  }), [activeMetrics, backgroundHash, dimensionPlacementMode, dimensionStore, items, pushHistory]);

  const revokeUrlRef = useCallback((ref: React.MutableRefObject<string | null>) => {
    if (ref.current) {
      URL.revokeObjectURL(ref.current);
      ref.current = null;
    }
  }, []);

  const clearPendingRawUrl = useCallback(() => {
    revokeUrlRef(pendingRawUrlRef);
    setPendingRawUrl(null);
  }, [revokeUrlRef]);

  const clearPlanImage = useCallback((options?: { resetMeasurements?: boolean }) => {
    const resetMeasurements = options?.resetMeasurements ?? true;
    revokeUrlRef(rawUrlRef);
    revokeUrlRef(cleanedUrlRef);
    clearPendingRawUrl();
    setBgImageRaw(null);
    setBgImageUrl(null);
    setBgImage(null);
    setBackgroundHash(null);
    if (resetMeasurements) {
      setCalibration(null);
      setDimensions([]);
    }
    setBgTransform(DEFAULT_BG_TRANSFORM);
    setPositioningPlan(false);
    setSelected(null);
    setToolMode('select');
    setDimensionPreview(null);
    setPendingCalibrationSegment(null);
    setDimensionContextMenu(null);
    setCalibrationInput('');
    setCalibrationError(null);
    setCleanupOpen(false);
    setImageError(null);
  }, [clearPendingRawUrl, revokeUrlRef]);

  useEffect(() => {
    if (!bgImageUrl) {
      setBgImage(null);
      return;
    }

    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) {
        setBgImage(img);
        try {
          setBackgroundHash(computeBackgroundHash(imageToPngDataUrl(img)));
        } catch {
          setBackgroundHash(null);
        }
      }
    };
    img.onerror = () => {
      if (!cancelled) setBgImage(null);
    };
    img.src = bgImageUrl;

    return () => { cancelled = true; };
  }, [bgImageUrl]);

  useEffect(() => () => {
    revokeUrlRef(rawUrlRef);
    revokeUrlRef(cleanedUrlRef);
    revokeUrlRef(pendingRawUrlRef);
  }, [revokeUrlRef]);

  useEffect(() => {
    const clearPatchHash = () => {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    };

    const handlePatchHash = () => {
      if (!window.location.hash.includes('patchBlockJson')) return;

      const result = parseBlockPatchFromHash(window.location.hash);
      if (result.ok) {
        setEditingCustomBlockItemId(null);
        setCreatingCustomBlockItemId(null);
        customBlockCreationBaseItemsRef.current = null;
        setPendingAiBlockPatch({
          payload: result.payload,
          item: {
            id: `ai_patch_${crypto.randomUUID()}`,
            type: 'custom_block',
            u: 0.5,
            v: 0.5,
            lineId: null,
            rotation: 0,
            customBlock: result.payload,
          },
        });
      } else if (result.reason !== 'missing') {
        console.warn(`Invalid PanelForge block patch: ${result.reason}`);
      }

      clearPatchHash();
    };

    handlePatchHash();
    window.addEventListener('hashchange', handlePatchHash);
    return () => window.removeEventListener('hashchange', handlePatchHash);
  }, []);

  const handleItemsChange = useCallback((next: PlacedItem[]) => {
    pushHistory();
    onItemsChange(next);
  }, [onItemsChange, pushHistory]);

  const handleCanvasItemsChange = useCallback((next: PlacedItem[]) => {
    if (creatingCustomBlockItemId) {
      onItemsChange(next);
      return;
    }
    handleItemsChange(next);
  }, [creatingCustomBlockItemId, handleItemsChange, onItemsChange]);

  const handlePaletteSelect = useCallback((type: string | null) => {
    setSelected(type);
    setActiveItemId(null);
    setToolMode(type ? 'place_item' : 'select');
    setDimensionPreview(null);
    setPendingCalibrationSegment(null);
    setDimensionContextMenu(null);
  }, []);

  const handleToggleDimensionPanel = useCallback(() => {
    setSelected(null);
    setActiveItemId(null);
    setDimensionContextMenu(null);
    setDimensionHelpOpen(open => !open);
  }, []);

  const handleStartDimensionMode = useCallback(() => {
    setSelected(null);
    setPositioningPlan(false);
    setDimensionContextMenu(null);
    setDimensionHelpOpen(false);
    dimensionTool.requestDimensionMode();
    setToolMode(dimensionTool.mode);
    setDimensionPreview(dimensionTool.getPreview());
  }, [dimensionTool]);

  const handleDimensionPointerDown = useCallback((point: Pt, snapThresholdSourcePx: number) => {
    dimensionTool.onPointerDown(point, snapThresholdSourcePx);
    setDimensionPreview(dimensionTool.getPreview());
  }, [dimensionTool]);

  const handleDimensionPointerMove = useCallback((point: Pt, snapThresholdSourcePx: number) => {
    dimensionTool.onPointerMove(point, snapThresholdSourcePx);
    setDimensionPreview(dimensionTool.getPreview());
  }, [dimensionTool]);

  const handleDimensionPointerUp = useCallback((point: Pt, snapThresholdSourcePx: number) => {
    dimensionTool.onPointerUp(point, snapThresholdSourcePx);
    setDimensionPreview(dimensionTool.getPreview());
  }, [dimensionTool]);

  const handleDimensionOffsetDragStart = useCallback((dimensionId: string) => {
    if (dimensionOffsetHistoryRef.current === dimensionId) return;
    dimensionOffsetHistoryRef.current = dimensionId;
    pushHistory();
  }, [pushHistory]);

  const handleDimensionOffsetChange = useCallback((dimensionId: string, offsetPx: number) => {
    dimensionStore.setOffset(dimensionId, offsetPx);
    setDimensions(dimensionStore.serialize());
  }, [dimensionStore]);

  const handleDimensionOffsetDragEnd = useCallback(() => {
    dimensionOffsetHistoryRef.current = null;
  }, []);

  const handleDeleteAllDimensions = useCallback(() => {
    if (dimensions.length === 0) return;
    pushHistory();
    setDimensions([]);
    setDimensionsHidden(false);
    setDimensionHelpOpen(false);
    setDimensionContextMenu(null);
  }, [dimensions.length, pushHistory]);

  const handleDimensionContextMenu = useCallback((dimensionId: string, clientX: number, clientY: number) => {
    const dimension = dimensionViews.find(candidate => candidate.id === dimensionId);
    setDimensionContextMenu({
      id: dimensionId,
      x: clientX,
      y: clientY,
      labelInput: dimension?.labelOverride ?? dimension?.label ?? '',
    });
  }, [dimensionViews]);

  const removeDimensionFromMenu = useCallback(() => {
    if (!dimensionContextMenu) return;
    pushHistory();
    dimensionStore.remove(dimensionContextMenu.id);
    setDimensions(dimensionStore.serialize());
    setDimensionContextMenu(null);
  }, [dimensionContextMenu, dimensionStore, pushHistory]);

  const applyDimensionLabelFromMenu = useCallback(() => {
    if (!dimensionContextMenu) return;
    const label = dimensionContextMenu.labelInput.trim();
    pushHistory();
    dimensionStore.setLabelOverride(dimensionContextMenu.id, label || null);
    setDimensions(dimensionStore.serialize());
    setDimensionContextMenu(null);
  }, [dimensionContextMenu, dimensionStore, pushHistory]);

  const resetDimensionLabelFromMenu = useCallback(() => {
    if (!dimensionContextMenu) return;
    pushHistory();
    dimensionStore.setLabelOverride(dimensionContextMenu.id, null);
    setDimensions(dimensionStore.serialize());
    setDimensionContextMenu(null);
  }, [dimensionContextMenu, dimensionStore, pushHistory]);

  const cancelCalibrationInput = useCallback(() => {
    setPendingCalibrationSegment(null);
    setCalibrationInput('');
    setCalibrationError(null);
    setToolMode('select');
    dimensionTool.cancel();
    setDimensionPreview(null);
  }, [dimensionTool]);

  const applyCalibrationInput = useCallback(() => {
    if (!pendingCalibrationSegment || !backgroundHash) {
      setCalibrationError(t('floor_plan.calibration_no_background'));
      return;
    }
    const refLengthMm = parseLengthMm(calibrationInput, { locale: unitLocale });
    if (refLengthMm === null) {
      setCalibrationError(t('floor_plan.calibration_invalid'));
      return;
    }
    const segmentPx = Math.hypot(
      pendingCalibrationSegment.b.x - pendingCalibrationSegment.a.x,
      pendingCalibrationSegment.b.y - pendingCalibrationSegment.a.y,
    );
    if (segmentPx < 1) {
      setCalibrationError(t('floor_plan.calibration_too_short'));
      return;
    }

    const nextCalibration = makeUserCalibration({
      a: pendingCalibrationSegment.a,
      b: pendingCalibrationSegment.b,
      refLengthMm,
      backgroundHash,
      createdAt: new Date().toISOString(),
    });
    pushHistory();
    setCalibration(nextCalibration);
    activeMetrics.setCalibration(nextCalibration);
    setPendingCalibrationSegment(null);
    setCalibrationInput('');
    setCalibrationError(null);
    setToolMode('dimension');
    setDimensionPreview(null);
  }, [activeMetrics, backgroundHash, calibrationInput, pendingCalibrationSegment, pushHistory, t, unitLocale]);

  const handleClear = () => {
    pushHistory();
    setSelected(null);
    setToolMode('select');
    setDimensionPreview(null);
    setPendingCalibrationSegment(null);
    setDimensionContextMenu(null);
    setActiveItemId(null);
    setEditingCustomBlockItemId(null);
    setCreatingCustomBlockItemId(null);
    customBlockCreationBaseItemsRef.current = null;
    setDimensions([]);
    onItemsChange([]);
    onClearProject?.();
  };

  const handleUndo = () => {
    if (!history.length) return;
    const prev = history[history.length - 1];
    setRedoHistory(h => [...h.slice(-30), makeHistoryState()]);
    setHistory(h => h.slice(0, -1));
    restoreHistoryState(prev);
  };

  const handleRedo = () => {
    if (!redoHistory.length) return;
    const next = redoHistory[redoHistory.length - 1];
    setHistory(h => [...h.slice(-30), makeHistoryState()]);
    setRedoHistory(h => h.slice(0, -1));
    restoreHistoryState(next);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const file = input.files?.[0];
    if (!file) return;
    backgroundLoadSeqRef.current += 1;
    const validation = await validateImageFile(file);
    if (!validation.ok) {
      setImageError(validation.reason);
      input.value = '';
      return;
    }

    setImageError(null);
    const url = URL.createObjectURL(file);
    if (file.size > CLEANUP_AUTO_OPEN_SIZE) {
      clearPendingRawUrl();
      pendingRawUrlRef.current = url;
      setPendingRawUrl(url);
      setCleanupOpen(true);
    } else {
      clearPlanImage({ resetMeasurements: false });
      rawUrlRef.current = url;
      setBgImageRaw(url);
      setBgImageUrl(url);
      setBgTransform(DEFAULT_BG_TRANSFORM);
    }
    // Reset input so same file can be re-uploaded
    input.value = '';
  };

  const handleCleanupCancel = () => {
    clearPendingRawUrl();
    setCleanupOpen(false);
  };

  const handleCleanupApply = (cleanedUrl: string) => {
    const nextRawUrl = pendingRawUrlRef.current;
    if (nextRawUrl) {
      revokeUrlRef(rawUrlRef);
      rawUrlRef.current = nextRawUrl;
      pendingRawUrlRef.current = null;
      setPendingRawUrl(null);
      setBgImageRaw(nextRawUrl);
      setBgTransform(DEFAULT_BG_TRANSFORM);
    }

    revokeUrlRef(cleanedUrlRef);
    cleanedUrlRef.current = cleanedUrl;
    setBgImageUrl(cleanedUrl);
    setCleanupOpen(false);
  };

  const handleOpenCleanup = () => {
    if (!bgImageRaw) return;
    setCleanupOpen(true);
  };

  const handleRemovePlanImage = () => {
    clearPlanImage();
    onBackgroundCleared?.();
  };

  const handlePlanPositionChange = useCallback((nextBgTransform: BgTransform) => {
    setBgTransform(nextBgTransform);
  }, []);

  const handlePlanScale = useCallback((nextScale: number) => {
    setBgTransform(current => ({ ...current, scale: nextScale }));
  }, []);

  const handleResetPlanTransform = () => {
    setBgTransform(DEFAULT_BG_TRANSFORM);
  };

  const toggleLayerVisibility = useCallback((layer: FloorPlanLayer) => {
    setHiddenLayers(current => {
      const next = new Set(current);
      if (next.has(layer)) next.delete(layer);
      else next.add(layer);
      return next;
    });
  }, []);

  const showLayer = useCallback((layer: FloorPlanLayer) => {
    setHiddenLayers(current => {
      if (!current.has(layer)) return current;
      const next = new Set(current);
      next.delete(layer);
      return next;
    });
  }, []);

  const handleIconSizeChange = useCallback((nextIconSize: number) => {
    setIconSize(nextIconSize);
    const metrics = canvasRef.current?.getPlacementMetrics();
    const nextItems = reflowBlockGroups(items, nextIconSize, metrics);
    if (nextItems !== items) onItemsChange(nextItems);
  }, [items, onItemsChange]);

  const loadBackgroundDataUrl = useCallback(async (dataUrl: string, loadSeq: number, nextBgTransform: BgTransform) => {
    let nextUrl: string | null = null;
    try {
      nextUrl = await createObjectUrlFromDataUrl(dataUrl);
      if (loadSeq !== backgroundLoadSeqRef.current) {
        URL.revokeObjectURL(nextUrl);
        return;
      }
      revokeUrlRef(rawUrlRef);
      revokeUrlRef(cleanedUrlRef);
      clearPendingRawUrl();
      setCleanupOpen(false);
      setBgImage(null);
      rawUrlRef.current = nextUrl;
      setBgImageRaw(nextUrl);
      setBgImageUrl(nextUrl);
      setBackgroundHash(computeBackgroundHash(dataUrl));
      setImageError(null);
      setBgTransform(nextBgTransform);
      nextUrl = null;
    } catch {
      setImageError('not a valid image');
    } finally {
      if (nextUrl) URL.revokeObjectURL(nextUrl);
    }
  }, [clearPendingRawUrl, revokeUrlRef]);

  useImperativeHandle(ref, () => ({
    getState: () => ({
      lines,
      activeLineId,
      iconSize,
      gridEnabled,
      gridSize,
      bgTransform,
      background: null,
      backgroundHash,
      calibration,
      dimensions,
    }),
    loadState: (plan: PlanProjectState) => {
      const loadSeq = backgroundLoadSeqRef.current + 1;
      backgroundLoadSeqRef.current = loadSeq;
      setLines(plan.lines);
      setActiveLineId(plan.activeLineId);
      setIconSize(plan.iconSize);
      setGridEnabled(plan.gridEnabled);
      setGridSize(plan.gridSize);
      setBgTransform(plan.bgTransform);
      setBackgroundHash(plan.backgroundHash);
      setCalibration(plan.calibration);
      setDimensions(plan.dimensions);
      setHistory([]);
      setRedoHistory([]);
      setSelected(null);
      setToolMode('select');
      setDimensionPreview(null);
      setPendingCalibrationSegment(null);
      setDimensionContextMenu(null);
      setCalibrationInput('');
      setCalibrationError(null);
      setHighlightedLines(new Set());
      setMasterOff(false);
      setSavedHighlightedLines(new Set());
      setHiddenLayers(new Set());
      setEditingDoubleSwitchItemId(null);
      setEditingMasterSwitchItemId(null);
      setEditingCustomBlockItemId(null);
      setCreatingCustomBlockItemId(null);
      customBlockCreationBaseItemsRef.current = null;
      setPendingAiBlockPatch(null);
      setActiveItemId(null);
      setPositioningPlan(false);
      setCleanupOpen(false);
      setTransform({ scale: 1, offsetX: 0, offsetY: 0 });
      onItemsChange(plan.items);
      if (plan.background) {
        void loadBackgroundDataUrl(plan.background, loadSeq, plan.bgTransform);
      } else {
        clearPlanImage();
      }
    },
    exportBackgroundDataUrl: async () => {
      if (!bgImage) return null;
      return imageToPngDataUrl(bgImage);
    },
    openSpecification: () => setSpecificationOpen(true),
  }), [
    activeLineId,
    bgImage,
    bgTransform,
    backgroundHash,
    calibration,
    clearPlanImage,
    dimensions,
    gridEnabled,
    gridSize,
    iconSize,
    lines,
    loadBackgroundDataUrl,
    onItemsChange,
  ]);

  useEffect(() => {
    onProjectStateChange?.();
  }, [
    activeLineId,
    bgImage,
    bgImageUrl,
    backgroundHash,
    bgTransform,
    calibration,
    dimensions,
    gridEnabled,
    gridSize,
    iconSize,
    lines,
    onProjectStateChange,
  ]);

  const handleAddLine = () => {
    const usedColors = new Set(lines.map(line => line.color));
    const color = LINE_COLORS.find(candidate => !usedColors.has(candidate));
    if (!color) return;

    let nextIndex = 1;
    while (lines.some(line => line.id === `L${nextIndex}`)) nextIndex += 1;
    const id = `L${nextIndex}`;
    setLines(current => [...current, { id, name: id, color }]);
    setActiveLineId(id);
  };

  const handleRenameLine = (lineId: string, name: string) => {
    const nextName = name.trim();
    if (!nextName) return;
    setLines(current => current.map(line => line.id === lineId ? { ...line, name: nextName } : line));
  };

  const handleDeleteLine = (lineId: string) => {
    if (lines.length <= 1) return;
    const remainingLines = lines.filter(line => line.id !== lineId);
    if (remainingLines.length === 0) return;

    pushHistory();
    setLines(remainingLines);
    setHighlightedLines(current => {
      const next = new Set(current);
      next.delete(lineId);
      return next;
    });
    if (activeLineId === lineId) setActiveLineId(remainingLines[0].id);
    onItemsChange(items.flatMap(item => {
      if (item.type === 'switch_double') {
        return [{
          ...item,
          switchLines: {
            left: item.switchLines?.left === lineId ? null : item.switchLines?.left ?? null,
            right: item.switchLines?.right === lineId ? null : item.switchLines?.right ?? null,
          },
        }];
      }
      return !isLineControlledItemType(item.type) || item.lineId !== lineId ? [item] : [];
    }));
  };

  const updateDoubleSwitchLine = (itemId: string, side: 'left' | 'right', lineId: string | null) => {
    handleItemsChange(items.map(item => {
      if (item.id !== itemId || item.type !== 'switch_double') return item;
      return {
        ...item,
        lineId: null,
        switchLines: {
          left: item.switchLines?.left ?? null,
          right: item.switchLines?.right ?? null,
          [side]: lineId,
        },
      };
    }));
  };

  const startCustomBlockCreation = () => {
    const placeholder: PlacedItem = {
      id: crypto.randomUUID(),
      type: 'custom_block',
      u: 0.5,
      v: 0.5,
      lineId: null,
      rotation: 0,
    };

    customBlockCreationBaseItemsRef.current = items;
    onItemsChange([...items, placeholder]);
    setActiveItemId(placeholder.id);
    setEditingCustomBlockItemId(placeholder.id);
    setCreatingCustomBlockItemId(placeholder.id);
    setSelected(null);
    setToolMode('select');
    setDimensionPreview(null);
    setPendingCalibrationSegment(null);
  };

  const createBlockItems = (
    marker: PlacedItem,
    payload: CustomBlockPayload,
    options?: { activeLineId?: string | null },
  ): PlacedItem[] => {
    const mechanisms = payload.mechanisms
      .map(mechanism => ({ mechanism, placedType: mechanismToPlacedType(mechanism.type) }))
      .filter((entry): entry is { mechanism: CustomBlockPayload['mechanisms'][number]; placedType: string } => Boolean(entry.placedType));
    const placedMechanisms = mechanisms.length
      ? mechanisms
      : [{ mechanism: { type: 'socket' as const }, placedType: 'socket_single' }];
    const blockId = `block_${crypto.randomUUID()}`;
    const metrics = canvasRef.current?.getPlacementMetrics();
    const uSpacing = metrics?.width ? (iconSize * BLOCK_SLOT_SPACING_FACTOR) / metrics.width : FALLBACK_BLOCK_ITEM_SPACING;
    const vSpacing = metrics?.height ? (iconSize * BLOCK_SLOT_SPACING_FACTOR) / metrics.height : FALLBACK_BLOCK_ITEM_SPACING;
    const uPositions = payload.orientation === 'horizontal'
      ? blockAxisPositions(marker.u, placedMechanisms.length, uSpacing)
      : placedMechanisms.map(() => clampPlanPosition(marker.u));
    const vPositions = payload.orientation === 'vertical'
      ? blockAxisPositions(marker.v, placedMechanisms.length, vSpacing)
      : placedMechanisms.map(() => clampPlanPosition(marker.v));

    return placedMechanisms.map(({ mechanism, placedType }, index) => ({
      id: crypto.randomUUID(),
      type: placedType,
      u: uPositions[index],
      v: vPositions[index],
      lineId: getBlockItemLineId(placedType, mechanism.lineId, options?.activeLineId),
      rotation: marker.rotation ?? 0,
      blockId,
    }));
  };

  const updateCustomBlock = (payload: CustomBlockPayload) => {
    const itemId = editingCustomBlockItemId;
    if (!itemId) return;
    const marker = items.find(item => item.id === itemId && item.type === 'custom_block');
    if (!marker) return;

    const expandedItems = createBlockItems(marker, payload, { activeLineId: activeLineId || DEFAULT_LINE_ID });
    if (itemId === creatingCustomBlockItemId) {
      pushHistory({
        ...makeHistoryState(),
        items: customBlockCreationBaseItemsRef.current ?? items.filter(item => item.id !== itemId),
      });
      onItemsChange(items.flatMap(item => item.id === itemId ? expandedItems : [item]));
    } else {
      handleItemsChange(items.flatMap(item => item.id === itemId ? expandedItems : [item]));
    }
    setActiveItemId(expandedItems[0]?.id ?? null);
    setEditingCustomBlockItemId(null);
    setCreatingCustomBlockItemId(null);
    customBlockCreationBaseItemsRef.current = null;
  };

  const closeCustomBlockBuilder = () => {
    const itemId = editingCustomBlockItemId;
    if (itemId && itemId === creatingCustomBlockItemId) {
      onItemsChange(customBlockCreationBaseItemsRef.current ?? items.filter(item => item.id !== itemId));
      if (activeItemId === itemId) setActiveItemId(null);
      setCreatingCustomBlockItemId(null);
      setEditingCustomBlockItemId(null);
      customBlockCreationBaseItemsRef.current = null;
      return;
    }

    setEditingCustomBlockItemId(null);
  };

  const createAiBlockPatch = (payload: CustomBlockPayload) => {
    if (!pendingAiBlockPatch) return;

    const expandedItems = createBlockItems(pendingAiBlockPatch.item, payload);
    handleItemsChange([...items, ...expandedItems]);
    setActiveItemId(expandedItems[0]?.id ?? null);
    setPendingAiBlockPatch(null);
  };

  const closeAiBlockPatch = () => {
    setPendingAiBlockPatch(null);
  };

  const rotateActiveItem = useCallback(() => {
    if (!activeItemId) return;
    handleCanvasItemsChange(items.map(item => item.id === activeItemId
      ? { ...item, rotation: nextRotation(item.rotation) }
      : item,
    ));
  }, [activeItemId, handleCanvasItemsChange, items]);

  const toggleLineHighlight = (lineId: string) => {
    // A realistic master switch held off blocks all line switching.
    if (masterOff) return;
    setActiveLineId(lineId);
    setHighlightedLines(current => {
      const next = new Set(current);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const clearHighlights = useCallback(() => {
    setHighlightedLines(new Set());
  }, []);

  const operateMasterSwitch = useCallback((itemId: string) => {
    const item = items.find(it => it.id === itemId);
    if (!item || item.type !== 'master_switch') return;
    if ((item.masterMode ?? 'simple') !== 'realistic') {
      // Simple mode: just turn everything off.
      setMasterOff(false);
      setSavedHighlightedLines(new Set());
      clearHighlights();
      return;
    }
    // Realistic mode: toggle, remembering the last lit lines.
    if (masterOff) {
      setHighlightedLines(new Set(savedHighlightedLines));
      setMasterOff(false);
    } else {
      setSavedHighlightedLines(new Set(highlightedLines));
      setHighlightedLines(new Set());
      setMasterOff(true);
    }
  }, [clearHighlights, highlightedLines, items, masterOff, savedHighlightedLines]);

  const setMasterSwitchMode = useCallback((itemId: string, mode: MasterSwitchMode) => {
    // Leaving realistic mode releases any held-off state.
    if (mode !== 'realistic') {
      setMasterOff(false);
      setSavedHighlightedLines(new Set());
    }
    handleCanvasItemsChange(items.map(item =>
      item.id === itemId && item.type === 'master_switch'
        ? { ...item, masterMode: mode }
        : item,
    ));
  }, [handleCanvasItemsChange, items]);

  useEffect(() => {
    if (activeItemId && !items.some(item => item.id === activeItemId)) {
      setActiveItemId(null);
    }
    if (creatingCustomBlockItemId && !items.some(item => item.id === creatingCustomBlockItemId)) {
      setCreatingCustomBlockItemId(null);
      if (editingCustomBlockItemId === creatingCustomBlockItemId) {
        setEditingCustomBlockItemId(null);
      }
      customBlockCreationBaseItemsRef.current = null;
    }
  }, [activeItemId, creatingCustomBlockItemId, editingCustomBlockItemId, items]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isTextEditingTarget(event.target)) return;

      if (event.key === 'Escape') {
        if (pendingCalibrationSegment) {
          cancelCalibrationInput();
          return;
        }
        if (toolMode === 'dimension' || toolMode === 'calibrate') {
          dimensionTool.cancel();
          setToolMode('select');
          setDimensionPreview(null);
          return;
        }
        if (positioningPlan) {
          setPositioningPlan(false);
          return;
        }
        clearHighlights();
      }
      if (
        event.key.toLowerCase() === 'r' &&
        activeItemId &&
        !event.altKey &&
        !event.ctrlKey &&
        !event.metaKey
      ) {
        event.preventDefault();
        rotateActiveItem();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeItemId, cancelCalibrationInput, clearHighlights, dimensionTool, pendingCalibrationSegment, positioningPlan, rotateActiveItem, toolMode]);

  const stats = computeStats(items);
  const specification = useMemo(
    () => buildElectricalSpecification(items, lines),
    [items, lines],
  );
  const calibrationStale = Boolean(calibration && backgroundHash && activeMetrics.isStale(backgroundHash));
  const calibrationLabel = !calibration
    ? t('floor_plan.scale_unset')
    : calibrationStale
      ? t('floor_plan.scale_stale')
      : t('floor_plan.scale_value', { value: formatScaleMmPerPx(1 / calibration.pxPerMm, unitLocale) });
  const activeCustomBlockItem = items.find(
    item => item.id === activeItemId && item.type === 'custom_block',
  ) ?? null;
  const editingDoubleSwitchItem = items.find(
    item => item.id === editingDoubleSwitchItemId && item.type === 'switch_double',
  ) ?? null;
  const editingCustomBlockItem = items.find(
    item => item.id === editingCustomBlockItemId && item.type === 'custom_block',
  ) ?? null;
  const editingMasterSwitchItem = items.find(
    item => item.id === editingMasterSwitchItemId && item.type === 'master_switch',
  ) ?? null;

  const btnCls = `text-[11px] font-medium px-2.5 py-1.5 rounded border border-border-2
                  text-ink-3 hover:text-ink hover:bg-surface-3
                  transition-colors flex items-center gap-1.5`;

  return (
    <div className="flex h-[720px] flex-col overflow-hidden rounded-lg border border-border bg-surface-2">

      {/* ── Topbar ── */}
      <div className="no-print flex items-center gap-2 px-3 py-2 border-b border-border bg-surface flex-shrink-0">

        {/* Upload */}
        <label className={btnCls + ' cursor-pointer'}>
          <UploadIcon />
          {t('floor_plan.upload_plan')}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={handleFileChange}
          />
        </label>

        {/* Clear */}
        <button className={btnCls} onClick={handleClear}>
          <ClearIcon />
          {t('floor_plan.clear')}
        </button>

        {/* Undo */}
        <button className={btnCls} onClick={handleUndo} disabled={!history.length}>
          <UndoIcon />
          {t('floor_plan.undo')}
        </button>
        <button className={btnCls} onClick={handleRedo} disabled={!redoHistory.length}>
          <RedoIcon />
          {t('floor_plan.redo')}
        </button>

        <button
          className={btnCls}
          onClick={clearHighlights}
          disabled={highlightedLines.size === 0}
          title={t('floor_plan.clear_highlight')}
        >
          {t('floor_plan.clear_highlight')}
          <span className="font-mono text-[11px] text-ink-4">{highlightedLines.size}</span>
        </button>

        <div className="flex items-center gap-1 border-l border-border pl-2">
          <button
            className={[
              btnCls,
              'h-8 w-8 justify-center px-0',
              activeItemId ? 'border-accent/40 bg-accent/10 text-accent hover:text-accent' : 'opacity-50',
            ].join(' ')}
            onClick={rotateActiveItem}
            disabled={!activeItemId}
            aria-label={t('floor_plan.rotate_icon')}
            title={t('floor_plan.rotate_icon')}
          >
            <span aria-hidden="true" className="text-sm leading-none">↻</span>
          </button>
          {activeCustomBlockItem && activeCustomBlockItem.id !== editingCustomBlockItemId && (
            <button
              className={[btnCls, 'border-accent/40 bg-accent/10 text-accent hover:text-accent'].join(' ')}
              onClick={() => setEditingCustomBlockItemId(activeCustomBlockItem.id)}
              title={t('floor_plan.configure_block')}
            >
              {t('floor_plan.configure_block')}
            </button>
          )}
        </div>

        {bgImageRaw && (
          <>
            <button className={btnCls} onClick={handleOpenCleanup}>
              {t('floor_plan.clean_plan')}
            </button>
            <button className={btnCls} onClick={handleRemovePlanImage}>
              {t('floor_plan.remove_plan')}
            </button>
          </>
        )}

        <div className="ml-auto flex items-center gap-2 border-l border-border pl-2">
          <button
            className={[
              btnCls,
              toolMode === 'dimension' || toolMode === 'calibrate'
                ? 'border-accent/40 bg-accent/10 text-accent hover:text-accent'
                : '',
              !bgImage ? 'opacity-50' : '',
            ].join(' ')}
            onClick={handleToggleDimensionPanel}
            disabled={!bgImage}
            title={t('floor_plan.dimension_tool')}
          >
            {t('floor_plan.dimension_tool')}
          </button>
          {dimensionHelpOpen && (
            <div className="fixed right-4 top-16 z-50 w-72 rounded-md border border-border bg-surface p-3 text-xs text-ink shadow-lg">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="font-semibold text-ink">{t('floor_plan.dimension_help_title')}</div>
                <button
                  type="button"
                  className="rounded px-1.5 py-0.5 text-ink-4 hover:bg-surface-3 hover:text-ink"
                  onClick={() => setDimensionHelpOpen(false)}
                  aria-label={t('floor_plan.close')}
                  title={t('floor_plan.close')}
                >
                  ×
                </button>
              </div>
              <ol className="space-y-1.5 text-ink-3">
                <li>{t('floor_plan.dimension_help_calibrate')}</li>
                <li>{t('floor_plan.dimension_help_enter')}</li>
                <li>{t('floor_plan.dimension_help_draw')}</li>
              </ol>
              <div className="mt-2 rounded bg-surface-3 px-2 py-1.5 text-[11px] text-ink-4">
                {t('floor_plan.dimension_help_tip')}
              </div>
              <div className="mt-3 border-t border-border pt-3">
                <div className="mb-1.5 text-[11px] font-medium text-ink-4">
                  {t('floor_plan.dimension_mode_title')}
                </div>
                <div className="grid grid-cols-2 overflow-hidden rounded border border-border-2 text-[11px]">
                  {(['free', 'orthogonal'] as const).map(mode => (
                    <button
                      key={mode}
                      type="button"
                      className={[
                        'px-2 py-1.5 font-medium transition-colors',
                        dimensionPlacementMode === mode
                          ? 'bg-accent/10 text-accent'
                          : 'text-ink-4 hover:bg-surface-3 hover:text-ink',
                      ].join(' ')}
                      onClick={() => {
                        setDimensionPlacementMode(mode);
                      }}
                      aria-pressed={dimensionPlacementMode === mode}
                    >
                      {t(`floor_plan.dimension_mode_${mode}`)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-3 border-t border-border pt-3">
                <div className="mb-1.5 text-[11px] font-medium text-ink-4">
                  {t('floor_plan.dimension_layer_title')}
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    type="button"
                    className={[
                      'rounded border border-border-2 px-2 py-1.5 text-[11px] font-medium transition-colors',
                      dimensionsHidden
                        ? 'bg-accent/10 text-accent'
                        : 'text-ink-3 hover:bg-surface-3 hover:text-ink',
                    ].join(' ')}
                    onClick={() => setDimensionsHidden(value => !value)}
                    disabled={dimensions.length === 0}
                  >
                    {dimensionsHidden ? t('floor_plan.dimension_show_all') : t('floor_plan.dimension_hide_all')}
                  </button>
                  <button
                    type="button"
                    className="rounded border border-danger/40 bg-danger/10 px-2 py-1.5 text-[11px] font-medium text-danger transition-colors hover:bg-danger/15 disabled:opacity-50"
                    onClick={handleDeleteAllDimensions}
                    disabled={dimensions.length === 0}
                  >
                    {t('floor_plan.dimension_delete_all')}
                  </button>
                </div>
              </div>
              <button
                type="button"
                className="mt-3 w-full rounded border border-accent/40 bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent hover:bg-accent/15"
                onClick={handleStartDimensionMode}
              >
                {t('floor_plan.dimension_start')}
              </button>
            </div>
          )}
          <span
            className={[
              'rounded bg-surface-3 px-2 py-1 text-[11px]',
              calibrationStale ? 'text-danger' : calibration ? 'text-ink-3' : 'text-ink-4',
            ].join(' ')}
            title={t('floor_plan.scale_status')}
          >
            {calibrationLabel}
          </span>
        </div>
      </div>

      {imageError && (
        <div className="border-b border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
          {imageError}
        </div>
      )}

      {/* ── Body ── */}
      <div className="flex flex-1 min-h-0">

        {/* Tools */}
        <div className="no-print flex w-48 min-w-[188px] flex-col border-r border-border bg-surface-2">
          <LinesPanel
            lines={lines}
            activeLineId={activeLineId}
            items={items}
            onSelectLine={setActiveLineId}
            onAddLine={handleAddLine}
            onRenameLine={handleRenameLine}
            onDeleteLine={handleDeleteLine}
          />
          <div className="min-h-0 flex-1 [&>div]:h-full">
            <ElementPalette
              selected={selected}
              onSelect={handlePaletteSelect}
              onStartCustomBlock={startCustomBlockCreation}
              hiddenLayers={hiddenLayers}
              onToggleLayer={toggleLayerVisibility}
              onShowLayer={showLayer}
              iconSize={iconSize}
              onIconSizeChange={handleIconSizeChange}
            />
          </div>
        </div>

        {/* Canvas area */}
        <div className="flex-1 relative min-w-0">
          {(toolMode === 'dimension' || toolMode === 'calibrate') && (
            <div className="no-print pointer-events-none absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-md border border-accent/30 bg-surface/95 px-3 py-2 text-xs text-ink shadow-lg backdrop-blur-sm">
              <div className="font-semibold text-accent">
                {toolMode === 'calibrate' ? t('floor_plan.mode_calibrate') : t('floor_plan.mode_dimension')}
              </div>
              <div className="mt-0.5 text-[11px] text-ink-4">
                {toolMode === 'calibrate'
                  ? t('floor_plan.dimension_mode_hint_calibrate')
                  : t('floor_plan.dimension_mode_hint_dimension')}
              </div>
            </div>
          )}
          <div
            className="no-print absolute left-3 top-3 z-10 flex flex-col items-center gap-3 rounded-lg border border-border bg-surface/95 p-3 text-ink shadow-lg backdrop-blur-sm"
            onPointerDown={event => event.stopPropagation()}
            onClick={event => event.stopPropagation()}
          >
            <button
              type="button"
              className={[
                'rounded border px-2.5 py-1.5 text-base leading-none transition-colors',
                workspaceLocked
                  ? 'border-accent/40 bg-accent/10 text-accent'
                  : 'border-border-2 text-ink-3 hover:bg-surface-3 hover:text-ink',
              ].join(' ')}
              onClick={() => setWorkspaceLocked(value => !value)}
              aria-pressed={workspaceLocked}
              aria-label={t(workspaceLocked ? 'floor_plan.unlock_workspace' : 'floor_plan.lock_workspace')}
              title={t(workspaceLocked ? 'floor_plan.unlock_workspace' : 'floor_plan.lock_workspace')}
            >
              {workspaceLocked ? '🔒' : '🔓'}
            </button>

            {bgImageRaw && (
              <>
                <button
                  type="button"
                  className={[
                    'rounded border px-2.5 py-1.5 text-[11px] font-medium transition-colors',
                    positioningPlan
                      ? 'border-accent/40 bg-accent/10 text-accent'
                      : 'border-border-2 text-ink-3 hover:bg-surface-3 hover:text-ink',
                  ].join(' ')}
                  onClick={() => setPositioningPlan(value => !value)}
                  aria-pressed={positioningPlan}
                  aria-label={t('floor_plan.position_plan')}
                  title={t('floor_plan.position_plan')}
                >
                  {t('floor_plan.position_plan')}
                </button>

                {positioningPlan && (
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-[11px] font-medium text-ink-4">{t('floor_plan.plan_scale')}</span>
                    <input
                      type="range"
                      min={30}
                      max={250}
                      step={1}
                      value={Math.round(bgTransform.scale * 100)}
                      onChange={event => handlePlanScale(Number(event.target.value) / 100)}
                      className="h-32 accent-accent"
                      style={{ writingMode: 'vertical-lr' }}
                      aria-label={t('floor_plan.plan_scale')}
                    />
                    <span className="font-mono text-[11px] text-ink-4">
                      {Math.round(bgTransform.scale * 100)}%
                    </span>
                    <button
                      type="button"
                      className="rounded border border-border-2 px-2 py-1 text-[11px] font-medium text-ink-3 transition-colors hover:bg-surface-3 hover:text-ink"
                      onClick={handleResetPlanTransform}
                      title={t('floor_plan.reset_plan_position')}
                      aria-label={t('floor_plan.reset_plan_position')}
                    >
                      ↺
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <FloorPlanCanvas
            ref={canvasRef}
            selected={selected}
            iconSize={iconSize}
            gridEnabled={gridEnabled}
            gridSize={gridSize}
            items={items}
            onItemsChange={handleCanvasItemsChange}
            bgImage={bgImage}
            bgTransform={bgTransform}
            positioningPlan={positioningPlan}
            workspaceLocked={workspaceLocked}
            onPlanPositionChange={handlePlanPositionChange}
            transform={transform}
            onTransformChange={setTransform}
            lines={lines}
            activeLineId={activeLineId}
            highlightedLines={highlightedLines}
            hiddenLayers={hiddenLayers}
            activeItemId={activeItemId}
            onActivateItem={setActiveItemId}
            onToggleHighlight={toggleLineHighlight}
            onRotateActiveItem={rotateActiveItem}
            onStartDimensionMode={handleStartDimensionMode}
            canStartDimensionMode={Boolean(bgImage)}
            onConfigureDoubleSwitch={setEditingDoubleSwitchItemId}
            onConfigureMaster={setEditingMasterSwitchItemId}
            toolMode={toolMode}
            dimensionPreview={dimensionPreview}
            dimensions={dimensionsHidden ? [] : dimensionViews}
            onDimensionPointerDown={handleDimensionPointerDown}
            onDimensionPointerMove={handleDimensionPointerMove}
            onDimensionPointerUp={handleDimensionPointerUp}
            onDimensionOffsetDragStart={handleDimensionOffsetDragStart}
            onDimensionOffsetChange={handleDimensionOffsetChange}
            onDimensionOffsetDragEnd={handleDimensionOffsetDragEnd}
            onDimensionContextMenu={handleDimensionContextMenu}
          />
          <DoubleSwitchDialog
            open={Boolean(editingDoubleSwitchItem)}
            item={editingDoubleSwitchItem}
            lines={lines}
            onChangeLine={updateDoubleSwitchLine}
            onToggleLine={toggleLineHighlight}
            onClose={() => setEditingDoubleSwitchItemId(null)}
          />
          <MasterSwitchDialog
            open={Boolean(editingMasterSwitchItem)}
            item={editingMasterSwitchItem}
            activeCount={highlightedLines.size}
            isOff={masterOff}
            savedCount={savedHighlightedLines.size}
            onSetMode={setMasterSwitchMode}
            onOperate={operateMasterSwitch}
            onClose={() => setEditingMasterSwitchItemId(null)}
          />
          <BlockBuilderDialog
            open={Boolean(editingCustomBlockItem)}
            item={editingCustomBlockItem}
            lines={lines}
            activeLineId={activeLineId || DEFAULT_LINE_ID}
            onSave={updateCustomBlock}
            onClose={closeCustomBlockBuilder}
          />
          <BlockBuilderDialog
            open={Boolean(pendingAiBlockPatch)}
            item={pendingAiBlockPatch?.item ?? null}
            initialPayload={pendingAiBlockPatch?.payload}
            sourceNote={pendingAiBlockPatch ? t('floor_plan.ai_patch_source_note') : undefined}
            onSave={createAiBlockPatch}
            onClose={closeAiBlockPatch}
          />
          {specificationOpen && (
            <SpecificationDialog
              specification={specification}
              lines={lines}
              onClose={() => setSpecificationOpen(false)}
            />
          )}
          {dimensionContextMenu && (
            <div
              className="fixed z-40 w-64 rounded-md border border-border bg-surface p-3 text-xs text-ink shadow-xl"
              style={{ left: dimensionContextMenu.x, top: dimensionContextMenu.y }}
              onMouseDown={event => event.stopPropagation()}
              onContextMenu={event => event.preventDefault()}
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="font-semibold">{t('floor_plan.dimension_menu_title')}</div>
                <button
                  type="button"
                  className="rounded px-1.5 py-0.5 text-ink-4 hover:bg-surface-3 hover:text-ink"
                  onClick={() => setDimensionContextMenu(null)}
                  aria-label={t('floor_plan.close')}
                  title={t('floor_plan.close')}
                >
                  ×
                </button>
              </div>
              <label className="block text-[11px] font-medium text-ink-4">
                {t('floor_plan.dimension_label')}
                <input
                  value={dimensionContextMenu.labelInput}
                  onChange={event => setDimensionContextMenu(current => current
                    ? { ...current, labelInput: event.target.value }
                    : current)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') applyDimensionLabelFromMenu();
                    if (event.key === 'Escape') setDimensionContextMenu(null);
                  }}
                  className="mt-1 w-full rounded border border-border-2 bg-surface-2 px-2 py-1.5 text-xs text-ink outline-none focus:border-accent"
                />
              </label>
              <div className="mt-3 grid grid-cols-3 gap-1.5">
                <button
                  type="button"
                  className="rounded border border-border-2 px-2 py-1.5 text-[11px] font-medium text-ink-3 hover:bg-surface-3 hover:text-ink"
                  onClick={applyDimensionLabelFromMenu}
                >
                  {t('floor_plan.dimension_apply_label')}
                </button>
                <button
                  type="button"
                  className="rounded border border-border-2 px-2 py-1.5 text-[11px] font-medium text-ink-3 hover:bg-surface-3 hover:text-ink"
                  onClick={resetDimensionLabelFromMenu}
                >
                  {t('floor_plan.dimension_auto_label')}
                </button>
                <button
                  type="button"
                  className="rounded border border-danger/40 bg-danger/10 px-2 py-1.5 text-[11px] font-medium text-danger hover:bg-danger/15"
                  onClick={removeDimensionFromMenu}
                >
                  {t('floor_plan.dimension_delete')}
                </button>
              </div>
            </div>
          )}
          {pendingCalibrationSegment && (
            <div
              className="no-print absolute inset-0 z-30 flex items-center justify-center bg-black/20 p-4"
              onMouseDown={event => event.stopPropagation()}
            >
              <div className="w-full max-w-sm rounded-lg border border-border bg-surface p-4 shadow-xl">
                <div className="text-sm font-semibold text-ink">{t('floor_plan.calibration_title')}</div>
                <p className="mt-1 text-xs text-ink-4">{t('floor_plan.calibration_hint')}</p>
                <label className="mt-3 block text-[11px] font-medium text-ink-4">
                  {t('floor_plan.calibration_length')}
                  <input
                    value={calibrationInput}
                    onChange={event => {
                      setCalibrationInput(event.target.value);
                      setCalibrationError(null);
                    }}
                    onKeyDown={event => {
                      if (event.key === 'Enter') applyCalibrationInput();
                      if (event.key === 'Escape') cancelCalibrationInput();
                    }}
                    autoFocus
                    placeholder={t('floor_plan.calibration_placeholder')}
                    className="mt-1 w-full rounded border border-border-2 bg-surface-2 px-3 py-2 text-sm text-ink outline-none focus:border-accent"
                  />
                </label>
                {calibrationError && (
                  <div className="mt-2 rounded border border-danger/30 bg-danger/10 px-2 py-1.5 text-xs text-danger">
                    {calibrationError}
                  </div>
                )}
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded border border-border-2 px-3 py-1.5 text-xs font-medium text-ink-3 hover:bg-surface-3 hover:text-ink"
                    onClick={cancelCalibrationInput}
                  >
                    {t('floor_plan.calibration_cancel')}
                  </button>
                  <button
                    type="button"
                    className="rounded border border-accent/40 bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent hover:bg-accent/15"
                    onClick={applyCalibrationInput}
                  >
                    {t('floor_plan.calibration_apply')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Hint overlay */}
          {items.length === 0 && !bgImage && (
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none gap-3">
              <p className="text-sm text-ink-3 text-center leading-relaxed">
                {t('floor_plan.empty_upload_plan')}<br/>
                <span className="text-ink-4">{t('floor_plan.empty_place_items')}</span>
              </p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="pointer-events-auto inline-flex items-center gap-2 rounded-md border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-medium text-accent hover:bg-accent/15 transition-colors"
              >
                <UploadIcon />
                {t('floor_plan.upload_plan')}
              </button>
              <p className="text-[11px] text-ink-4">
                {t('floor_plan.empty_controls')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Statusbar ── */}
      <div className="no-print flex items-center border-t border-border bg-surface flex-shrink-0">
        <StatChip icon={<LightIcon />} label={t('floor_plan.stat_lights')} value={stats.light} />
        <StatChip icon={<SocketIcon />} label={t('floor_plan.stat_sockets')} value={stats.socket} />
        <StatChip icon={<SwitchIcon />} label={t('floor_plan.stat_switches')} value={stats.sw} />
        <StatChip icon={<ApplianceIcon />} label={t('floor_plan.stat_appliances')} value={stats.appliance} />
        <div className="flex items-center gap-1.5 px-4 text-[11px]">
          <span className="text-ink-4">∑</span>
          <span className="font-mono text-ink font-medium">{t('floor_plan.kw_value', { value: stats.kw.toFixed(1) })}</span>
        </div>
      </div>

      <ImageCleanupDialog
        open={cleanupOpen}
        sourceUrl={pendingRawUrl ?? bgImageRaw}
        onCancel={handleCleanupCancel}
        onApply={handleCleanupApply}
      />
    </div>
  );
});

// ─── Small components ─────────────────────────────────────────────────────────

function StatChip({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="flex items-center gap-1.5 px-3 py-2 border-r border-border text-[11px] text-ink-3">
      <span className="opacity-60">{icon}</span>
      <span className="font-mono font-medium text-ink">{value}</span>
      <span>{label}</span>
    </div>
  );
}

// ─── Inline SVG icons for topbar / statusbar ──────────────────────────────────

function UploadIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path d="M6.5 1v7M4 3.5L6.5 1 9 3.5M2 9.5v1.5a1 1 0 001 1h7a1 1 0 001-1V9.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path d="M2 2l9 9M11 2L2 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

function UndoIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path d="M2 5h6a3 3 0 010 6H5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M2 5l2.5-2.5M2 5l2.5 2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function RedoIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path d="M11 5H5a3 3 0 000 6h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M11 5L8.5 2.5M11 5L8.5 7.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function LightIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <circle cx="6" cy="6" r="3" stroke="#f59e0b" strokeWidth="1.2"/>
      <circle cx="6" cy="6" r="1" fill="#f59e0b"/>
    </svg>
  );
}

function SocketIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <circle cx="6" cy="6" r="4" stroke="#06b6d4" strokeWidth="1.2"/>
      <circle cx="4.5" cy="5.5" r="0.9" fill="#06b6d4"/>
      <circle cx="7.5" cy="5.5" r="0.9" fill="#06b6d4"/>
    </svg>
  );
}

function SwitchIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <rect x="2" y="2" width="8" height="8" rx="1.5" stroke="#8b9dc3" strokeWidth="1.2"/>
    </svg>
  );
}

function ApplianceIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <rect x="2" y="2" width="8" height="8" rx="1.5" stroke="#10b981" strokeWidth="1.2"/>
      <circle cx="6" cy="6" r="2" stroke="#10b981" strokeWidth="1"/>
    </svg>
  );
}
